import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export type ScheduleFormat = 'pdf' | 'xlsx' | 'csv' | 'html' | 'json';

export type DeliveryChannel =
  | {
      kind: 'email';
      recipients: string[];
      subject?: string;
      /** CSAGE-357: zip the attachment before sending. Absent = attach as-is. */
      compress?: 'zip';
    }
  | { kind: 'webhook'; url: string; bearer?: string }
  | {
      // CSAGE-268: SFTP delivery (backend schedule.types.ts sftp channel).
      kind: 'sftp';
      host: string;
      port?: number;
      username: string;
      path: string;
      /** Names a tenant-scoped SSH credential resolved at delivery time — never the key. */
      secretRef: string;
      /** Optional Jasper-style date stamp before the extension, e.g. yyyyMMdd. */
      filenamePattern?: string;
      /**
       * CSAGE-358: exact remote filename stem, used verbatim before the stamp.
       * Set when the receiving system matches on a fixed name (Payouts Network
       * ingests `Canopy_AML_Transactions-<yyyyMMdd>.csv`).
       */
      filenameBase?: string;
    };

export interface DeliveryConfig {
  format: ScheduleFormat;
  params: Record<string, unknown>;
  channels: DeliveryChannel[];
  /**
   * CSAGE-358 follow-up: artifact filename stem applied to EVERY channel, so one
   * subscription emits one name. Set → `<filenameBase>-<yyyyMMdd>.<ext>`.
   * A channel-level `filenameBase` (SFTP only) still overrides it.
   */
  filenameBase?: string;
}

export interface Schedule {
  id: string;
  tenantId: string;
  name: string;
  artifactType: 'report_template' | 'report' | 'dashboard';
  artifactId: string;
  cron: string;
  tz: string;
  enabled: boolean;
  delivery: DeliveryConfig;
  lastRunAt: string | null;
  nextRunAt: string | null;
  lastStatus: string | null;
  createdAt: string;
  updatedAt: string;
  /** Joined by the tenant-wide list for display/scoping. */
  artifactName?: string | null;
  artifactScope?: string | null;
  /** Backing report's owner email; null for migrated subs w/o owner. */
  ownerEmail?: string | null;
  /**
   * CSAGE-385: who created this subscription (`schedules.created_by`, stamped
   * from the session email at INSERT).
   *
   * NOT the same fact as `ownerEmail`, which describes the backing REPORT. They
   * routinely disagree: a customer subscribing to a shared catalog report gets
   * `createdBy = themselves` and `ownerEmail = whoever last edited the report`,
   * which is how the admin page came to attribute a Mood Media subscription to
   * a Banyan engineer. Null for every pre-CSAGE-385 row — unrecoverable, since
   * the audit trail nulls its actor for portal identities (CSAGE-367).
   */
  createdBy?: string | null;
  /**
   * CSAGE-384: the hierarchy node this subscription RENDERS AT — the schedule's
   * own captured scope, not the backing report's (`artifactScope`).
   *
   * For an inherited report subscribed from a child node the two legitimately
   * differ, and that difference is the whole bug: a subscription created at
   * Bentley against a report provisioned at Mood Media must run at Bentley. The
   * column was previously unreadable through any client, which is why a
   * mis-scoped subscription could not be diagnosed and the fault recurred.
   */
  scopePath?: string | null;
}

export interface Delivery {
  id: string;
  scheduleId: string;
  runId: string | null;
  channel: string;
  status: 'pending' | 'sent' | 'failed' | 'dead';
  attempts: number;
  lastError: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

export function useTemplateSchedules(templateId: string | null) {
  const api = useApi();
  return useQuery<Schedule[]>({
    queryKey: ['schedules', 'template', templateId],
    queryFn: () => api.fetch(`/report-templates/${templateId}/schedules`),
    enabled: !!templateId,
  });
}

/**
 * CSAGE-353: subscriptions bound to a report INSTANCE.
 *
 * Distinct from `useTemplateSchedules`, and the distinction is load-bearing:
 * `GET /schedules` (the admin Subscriptions page) only returns
 * `artifact_type = 'report'` rows for a scoped session — report-template
 * schedules carry no `scope_path`, so they are excluded fail-closed rather than
 * risk leaking another customer's subscription (schedule.service.ts listForTenant).
 *
 * A subscription created against a TEMPLATE therefore vanishes from the admin
 * page while still showing on the report that created it — which is exactly the
 * asymmetry reported from UAT. Consumer surfaces must use these instance hooks
 * so what they create is visible, attributable and manageable.
 */
export function useReportSchedules(reportId: string | null) {
  const api = useApi();
  return useQuery<Schedule[]>({
    queryKey: ['schedules', 'report', reportId],
    queryFn: () => api.fetch(`/reports/${reportId}/schedules`),
    enabled: !!reportId,
  });
}

/** CSAGE-353: create a subscription on a report instance. Body is identical to
 *  the template variant — the server takes reportId from the path. */
export function useCreateReportSchedule(reportId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      cron: string;
      tz: string;
      enabled: boolean;
      delivery: DeliveryConfig;
    }) =>
      api.fetch<Schedule>(`/reports/${reportId}/schedules`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useSchedule(scheduleId: string | null) {
  const api = useApi();
  return useQuery<Schedule>({
    queryKey: ['schedules', 'one', scheduleId],
    queryFn: () => api.fetch(`/schedules/${scheduleId}`),
    enabled: !!scheduleId,
  });
}

export function useAllSchedules(enabled = true) {
  const api = useApi();
  return useQuery<Schedule[]>({
    queryKey: ['schedules', 'all'],
    queryFn: () => api.fetch('/schedules'),
    refetchInterval: 10_000,
    // The endpoint is admin-role-gated (403 otherwise); callers that render
    // for non-admins too (e.g. the embed sidebar count) gate the fetch here
    // instead of letting it 403-poll every 10s.
    enabled,
  });
}

export function useScheduleDeliveries(scheduleId: string | null) {
  const api = useApi();
  return useQuery<Delivery[]>({
    queryKey: ['schedules', scheduleId, 'deliveries'],
    queryFn: () => api.fetch(`/schedules/${scheduleId}/deliveries`),
    enabled: !!scheduleId,
    refetchInterval: 5_000,
  });
}

export function useCreateSchedule(templateId: string | null) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      cron: string;
      tz: string;
      enabled: boolean;
      delivery: DeliveryConfig;
    }) =>
      api.fetch<Schedule>(`/report-templates/${templateId}/schedules`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useDeleteSchedule() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: string) =>
      api.fetchRaw(`/schedules/${scheduleId}`, { method: 'DELETE' }).then((r) => {
        if (!r.ok && r.status !== 204) throw new Error(`Delete failed: ${r.status}`);
        return true;
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useRunScheduleNow() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: string) =>
      api.fetch(`/schedules/${scheduleId}/run-now`, { method: 'POST' }),
    onSuccess: (_d, scheduleId) => {
      void qc.invalidateQueries({ queryKey: ['schedules'] });
      void qc.invalidateQueries({ queryKey: ['schedules', scheduleId, 'deliveries'] });
    },
  });
}

/**
 * Patch an existing schedule — name, cron/tz cadence, enabled, and/or the
 * whole delivery config (used for recipient edits). Any subset of fields.
 */
export function useUpdateSchedule() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      patch: Partial<Pick<Schedule, 'name' | 'cron' | 'tz' | 'enabled'>> & {
        delivery?: DeliveryConfig;
      };
    }) =>
      api.fetch<Schedule>(`/schedules/${input.id}`, {
        method: 'PATCH',
        body: JSON.stringify(input.patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useToggleSchedule() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; enabled: boolean }) =>
      api.fetch<Schedule>(`/schedules/${input.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: input.enabled }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}
